import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { Box, Stack, Typography, Paper, Divider, Chip } from '@mui/material';
import ButtonLoading from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Formik, FormikProps } from 'formik';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { EditPersonValidationSchema } from 'config/FormValidaton/TenantMaster/MasterTable/PersonTableValidationSchema';
import { PersonUsertRequest } from 'types/api/TenantMaster/MasterTable/PersonTableDataTypes';
import {
  useDeletePerson,
  usePersonDetails,
  useUpdatePerson,
} from 'services/v1/TenantMaster/MasterTable/PersonTableDataService';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { useParams, useSearchParams } from 'react-router-dom';
import { ModalResetPassword } from '../../components/ModalTablePersonData/ModalResetPasswordPersonComponent';
import ModalConfirmDeletePersonComponent from '../../components/ModalTablePersonData/ModalConfirmDeletePersonComponent';
import { ModalDeleteState } from '../../../../components/ModalComponent/ModalDeleteComponent';
import { getErrorMessage } from 'utils/Error';
import ActivityIndicator from 'components/ActivityIndicatorComponent';

const sectionPaperStyles = {
  p: 2.5,
  border: '1px solid #E3EBF6',
  borderRadius: 2,
  boxShadow: 'none',
};

const sectionHeaderStyles = {
  color: '#3B4797',
  fontWeight: 600,
  fontSize: 14,
};

const sectionDescriptionStyles = {
  color: '#6B7280',
  fontSize: 12,
  mt: 0.5,
};

const fieldLabelStyles = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  minWidth: 140,
  mb: 0.5,
};

const disabledInputStyles = {
  '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: '#F9FAFB' },
};

interface FormValue {
  person: string;
  fullName: string;
  friendlyName: string;
  mobile: string;
  email: string;
}

const formInitialValues: FormValue = {
  person: '',
  fullName: '',
  friendlyName: '',
  mobile: '',
  email: '',
};

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <Box mb={2}>
      <Typography sx={sectionHeaderStyles}>{title}</Typography>
      <Typography sx={sectionDescriptionStyles}>{description}</Typography>
    </Box>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5} sx={{ mt: 2 }}>
      <Typography sx={fieldLabelStyles}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </Typography>
      {children}
    </Stack>
  );
}

export default function EditPersonTableData() {
  const { id } = useParams();
  const formikRef = useRef<FormikProps<FormValue>>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutate: updatePerson, isLoading: isUpdating } = useUpdatePerson();
  const [showModalResetPassword, setShowModalResetPassword] = useState(false);
  const [modalDeleteState, setModalDeleteState] = useState<ModalDeleteState>({
    message: 'Are you sure you want to delete this data?',
    open: false,
  });
  const { data: personDetailsData, isLoading } = usePersonDetails(id || undefined);
  const { mutate: deletePerson } = useDeletePerson();

  const handleOnOpenModalResetPassword = () => {
    setShowModalResetPassword(true);
  };

  const handleOnSave = (data: FormValue) => {
    const payload: PersonUsertRequest = {
      id: id || '',
      fullName: data.fullName,
      friendlyName: data.friendlyName,
      mobile: data.mobile,
      userName: data.email,
      person: data.person,
    };

    updatePerson(payload, {
      onSuccess: () => {
        toast.success('Data submitted successfully!');
        formikRef.current?.resetForm();
        handleOnCancelInput();
      },
    });
  };

  const handleOnCancelInput = () => {
    const url =
      searchParams.get('refBackParam') != null
        ? searchParams.get('refBackParam')
        : PATH_CONSTANT.TENANT_MASTER.MASTER_TABLE.PERSON;
    const redirectPath = decodeURIComponent(url as string);
    navigate(redirectPath);
  };

  const handleOnCloseModalResetPassword = () => {
    setShowModalResetPassword(false);
  };

  const handleOnDeleteData = () => {
    if (id) {
      deletePerson(
        { userId: id },
        {
          onSuccess: () => {
            toast.success('Data deleted successfully!');
            navigate(PATH_CONSTANT.TENANT_MASTER.MASTER_TABLE.PERSON);
          },
          onError: (error) => {
            setModalDeleteState({
              open: true,
              message: '',
              isError: true,
              errorMessage: getErrorMessage(error),
            });
          },
        },
      );
    }
  };

  const handleOnShowDeleteConfirmation = () => {
    const message = `Are you sure you want to delete <strong>Person ${personDetailsData?.data?.person}</strong>?`;
    setModalDeleteState({ ...modalDeleteState, open: true, message });
  };

  useEffect(() => {
    if (personDetailsData) {
      formikRef.current?.setValues({
        person: personDetailsData.data.person,
        fullName: personDetailsData.data.fullName,
        friendlyName: personDetailsData.data.friendlyName || '',
        mobile: personDetailsData.data.mobile,
        email: personDetailsData.data.userName,
      });
    }
  }, [personDetailsData]);

  return (
    <TenantHomeDashboardNavigationBar>
      <Box sx={{ height: '100%', backgroundColor: '#F8F9FC' }}>
        {isLoading ? (
          <Stack direction='column' sx={{ height: 500 }} justifyContent='center'>
            <ActivityIndicator />
          </Stack>
        ) : (
          <Formik
            innerRef={formikRef}
            initialValues={formInitialValues}
            validationSchema={EditPersonValidationSchema}
            onSubmit={handleOnSave}
          >
            {(formikProps: FormikProps<{ [key: string]: string }>) => {
              const { values, handleChange, errors, touched, handleBlur } = formikProps;
              return (
                <Box sx={{ maxWidth: 820, mx: 'auto', py: 3, px: 2 }}>
                  {/* Page Header */}
                  <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
                    <Box>
                      <Stack direction='row' alignItems='center' spacing={1.5}>
                        <Typography
                          variant='body1'
                          component='h2'
                          fontWeight='bold'
                          sx={{ color: '#3B4797', fontSize: 18 }}
                        >
                          Edit Person
                        </Typography>
                        {personDetailsData?.data?.person && (
                          <Chip
                            label={personDetailsData.data.person}
                            size='small'
                            sx={{ backgroundColor: '#EDF2FF', color: '#3B4797', fontWeight: 500, fontSize: 12 }}
                          />
                        )}
                      </Stack>
                      <Typography sx={{ color: '#6B7280', fontSize: 13, mt: 0.5 }}>
                        Update this person's details and contact information. The person identifier cannot be changed
                        after creation.
                      </Typography>
                    </Box>
                    <Button
                      size='small'
                      onClick={handleOnCancelInput}
                      variant='main-table-panel-border'
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </Stack>

                  {/* Section 1: Personal Information */}
                  <Paper sx={sectionPaperStyles}>
                    <SectionHeader
                      title='Personal Information'
                      description='The person identifier was set during creation and cannot be changed. You can update the name fields.'
                    />
                    <Divider sx={{ mb: 1 }} />

                    <FormField label='Person' required>
                      <TextField
                        fullWidth
                        size='small'
                        disabled
                        placeholder='e.g. john.doe'
                        onChange={handleChange}
                        value={values?.person}
                        onBlur={handleBlur}
                        name='person'
                        error={touched.person && Boolean(errors.person)}
                        helperText={touched.person && errors.person}
                        sx={disabledInputStyles}
                      />
                    </FormField>

                    <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelStyles}>
                          First Name <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          disabled={isUpdating}
                          placeholder='e.g. John'
                          onChange={handleChange}
                          value={values?.fullName}
                          onBlur={handleBlur}
                          name='fullName'
                          error={touched.fullName && Boolean(errors.fullName)}
                          helperText={touched.fullName && errors.fullName}
                        />
                      </Stack>
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelStyles}>Family Name</Typography>
                        <TextField
                          fullWidth
                          size='small'
                          disabled={isUpdating}
                          placeholder='e.g. Doe'
                          onChange={handleChange}
                          value={values?.friendlyName}
                          onBlur={handleBlur}
                          name='friendlyName'
                          error={touched.friendlyName && Boolean(errors.friendlyName)}
                          helperText={touched.friendlyName && errors.friendlyName}
                        />
                      </Stack>
                    </Stack>
                  </Paper>

                  {/* Section 2: Contact Details */}
                  <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                    <SectionHeader
                      title='Contact Details'
                      description='Update the email address and mobile number. The email is used as the login username.'
                    />
                    <Divider sx={{ mb: 1 }} />

                    <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelStyles}>
                          Email <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          disabled={isUpdating}
                          placeholder='e.g. johndoe@email.com'
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values?.email}
                          name='email'
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                        />
                      </Stack>
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelStyles}>
                          Mobile Phone <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          disabled={isUpdating}
                          placeholder='e.g. 6123456789'
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.match(/^[0-9]*$/)) {
                              handleChange(e);
                            }
                          }}
                          onBlur={handleBlur}
                          value={values?.mobile}
                          name='mobile'
                          error={touched.mobile && Boolean(errors.mobile)}
                          helperText={touched.mobile && errors.mobile}
                        />
                      </Stack>
                    </Stack>
                  </Paper>

                  {/* Section 3: Security */}
                  <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                    <SectionHeader
                      title='Security'
                      description='Manage this user&apos;s password or remove the account entirely.'
                    />
                    <Divider sx={{ mb: 2 }} />

                    <Stack direction='row' spacing={2} alignItems='center'>
                      <Button
                        disabled={isUpdating}
                        variant='main-table-panel-border'
                        onClick={handleOnOpenModalResetPassword}
                        startIcon={<LockResetOutlinedIcon />}
                        size='small'
                      >
                        Reset Password
                      </Button>
                      <Button
                        disabled={isUpdating}
                        onClick={handleOnShowDeleteConfirmation}
                        variant='outlined'
                        color='error'
                        startIcon={<DeleteOutlineIcon />}
                        size='small'
                        sx={{ textTransform: 'none' }}
                      >
                        Delete Person
                      </Button>
                    </Stack>
                  </Paper>

                  {/* Action Buttons */}
                  <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 3 }}>
                    <Button disabled={isUpdating} onClick={handleOnCancelInput} variant='main-table-panel-border'>
                      Cancel
                    </Button>
                    <ButtonLoading
                      loading={isUpdating}
                      variant='main-table-panel'
                      onClick={() => formikProps.handleSubmit()}
                      // @ts-ignore
                      disabled={!EditPersonValidationSchema?.isValidSync(formikProps.values) || isUpdating}
                    >
                      Save Changes
                    </ButtonLoading>
                  </Stack>
                </Box>
              );
            }}
          </Formik>
        )}

        <ModalResetPassword
          visible={showModalResetPassword}
          userId={id || ''}
          onClose={handleOnCloseModalResetPassword}
        />
      </Box>
      <ModalConfirmDeletePersonComponent
        errorMessage={modalDeleteState.errorMessage}
        isError={modalDeleteState.isError}
        visible={modalDeleteState.open}
        title={undefined}
        message={modalDeleteState.message}
        onApprove={handleOnDeleteData}
        onClose={() => setModalDeleteState({ ...modalDeleteState, open: false, isError: false, errorMessage: '' })}
        onCancel={() => setModalDeleteState({ ...modalDeleteState, open: false, isError: false, errorMessage: '' })}
        userId={id}
      />
    </TenantHomeDashboardNavigationBar>
  );
}
